import {
  ConversationMessagePromptInfo,
  PromptDTO,
  PromptOwnerType,
} from "@shaarp/common";
import { LucideIcon, Sparkles } from "lucide-react";
import { CSSProperties } from "react";
import { CATEGORY_COLORS, ColorScheme, OWNER_COLORS } from "./prompt-colors";
import {
  getIconByName,
  getIconForOwnerType,
  getIconForPrompt,
} from "./prompt-icons";

export type SupportedLang = "fr" | "en";

export type PromptStyle = {
  Icon: LucideIcon;
  colors: ColorScheme;
};

export const getSupportedLang = (language: string): SupportedLang => {
  return language === "fr" ? "fr" : "en";
};

export const truncateContent = (
  content: string,
  maxLength: number = 150,
): string => {
  if (content.length <= maxLength) return content;
  return content.substring(0, maxLength).trim() + "...";
};

export const getPromptContent = (
  prompt: PromptDTO,
  lang: SupportedLang,
): string => {
  if (prompt.translations && prompt.translations[lang]) {
    return prompt.translations[lang].content;
  }
  return prompt.content;
};

export const getPromptTitle = (
  prompt: PromptDTO,
  lang: SupportedLang,
): string => {
  if (prompt.translations && prompt.translations[lang]) {
    return prompt.translations[lang].description;
  }
  return prompt.title || prompt.description || prompt.content.slice(0, 50);
};

export const getPromptSlug = (
  prompt: PromptDTO,
  lang: SupportedLang,
): string | undefined => {
  if (prompt.translations?.[lang]?.slug) {
    return prompt.translations[lang].slug;
  }
  return prompt.slug;
};

export const getPromptId = (prompt: PromptDTO): string => {
  return String(prompt.id);
};

/**
 * The tactic a message is sent with, as stored on the message and rendered in
 * its bubble. Its `promptId` is also what the API traces the tactic by (#681),
 * so every way of sending a tactic attaches one — the composer, the new-chat
 * suggestions and the company quick actions alike.
 *
 * @param userContent What the user typed beside the tactic; empty when the
 *   tactic is sent on its own.
 */
export const buildPromptSnapshot = (
  prompt: PromptDTO,
  userContent: string,
  lang: SupportedLang,
): ConversationMessagePromptInfo => ({
  promptId: getPromptId(prompt),
  title: getPromptTitle(prompt, lang),
  icon: prompt.icon,
  categoryId: prompt.categoryId,
  ownerType: prompt.ownerType,
  userContent,
});

/**
 * Entrance of a card in the suggestion grid: it rises into place behind the
 * cards before it, and drops the stagger on the way out so a whole row leaves at
 * once. Starts at `opacity: 0` because the delay would otherwise show the card
 * in its final spot before the animation claims it. The entrance follows the
 * `--cascade-duration`, `--cascade-start` and `--cascade-step` of
 * `suggestion-cascade`, so a screen can play the grid as one step of its own
 * cascade.
 */
export const getSuggestionCardAnimationStyle = (
  index: number,
  isTransitioning: boolean,
): CSSProperties => ({
  animation: isTransitioning
    ? "promptFadeOut 0.2s ease-out forwards"
    : `promptFadeInUp var(--cascade-duration, 0.4s) ease-out calc(var(--cascade-start, 0ms) + ${index} * var(--cascade-step, 60ms)) forwards`,
  opacity: isTransitioning ? 1 : 0,
});

export const isPublicPrompt = (prompt: PromptDTO): boolean => {
  return prompt.ownerType === "public";
};

export const getPromptStyle = (prompt: PromptDTO): PromptStyle => {
  const Icon = getIconForPrompt(prompt);
  const isPublic = prompt.ownerType === "public";
  const hasCategory = !!prompt.categoryId;

  const colors =
    isPublic && hasCategory
      ? CATEGORY_COLORS[prompt.categoryId!] || OWNER_COLORS.public
      : OWNER_COLORS[prompt.ownerType] || OWNER_COLORS.public;

  return { Icon, colors };
};

export const getHistoryItemStyle = (
  iconName?: string,
  categoryId?: string,
  ownerType?: PromptOwnerType,
): PromptStyle => {
  if (iconName) {
    const Icon = getIconByName(iconName);
    const colors = categoryId
      ? CATEGORY_COLORS[categoryId as keyof typeof CATEGORY_COLORS] ||
        OWNER_COLORS.public
      : OWNER_COLORS.public;
    return { Icon, colors };
  }

  if (ownerType) {
    const Icon = getIconForOwnerType(ownerType);
    const colors = OWNER_COLORS[ownerType] || OWNER_COLORS.public;
    return { Icon, colors };
  }

  return {
    Icon: Sparkles,
    colors: {
      border: "border-border",
      hoverBorder: "hover:border-border/50",
      bg: "",
      hoverBg: "hover:bg-accent/50",
      icon: "text-muted-foreground",
      iconBg: "bg-muted",
    },
  };
};

/**
 * The pinned tactics in the order the chat's tactic block shows them: the
 * catalogue's order, not the pin order. Shared so every surface that promises
 * "the same tactics as the chat, in the same order" cannot drift from it.
 */
export const getPinnedPromptsInCatalogueOrder = (
  prompts: readonly PromptDTO[],
  pinnedIds: ReadonlySet<string>,
): PromptDTO[] =>
  prompts.filter((prompt) => pinnedIds.has(getPromptId(prompt)));

export const generateRandomPrompts = (
  prompts: PromptDTO[],
  count: number = 3,
): PromptDTO[] => {
  const shuffled = [...prompts].sort(() => Math.random() - 0.5);
  const selected: PromptDTO[] = [];
  const usedCategories = new Set<string>();

  for (const prompt of shuffled) {
    const categoryId = prompt.categoryId || "other";
    if (!usedCategories.has(categoryId) && selected.length < count) {
      selected.push(prompt);
      usedCategories.add(categoryId);
    }
  }

  if (selected.length < count) {
    for (const prompt of shuffled) {
      if (!selected.includes(prompt) && selected.length < count) {
        selected.push(prompt);
      }
    }
  }

  return selected;
};

const PROMPT_USAGE_KEY = "prompt-usage";

export const recordPromptUsage = (promptId: string): void => {
  const stored = localStorage.getItem(PROMPT_USAGE_KEY);
  const usage: Record<string, string> = stored ? JSON.parse(stored) : {};
  usage[promptId] = new Date().toISOString();
  localStorage.setItem(PROMPT_USAGE_KEY, JSON.stringify(usage));
};

export const getPromptUsageMap = (): Record<string, string> => {
  const stored = localStorage.getItem(PROMPT_USAGE_KEY);
  return stored ? JSON.parse(stored) : {};
};

export const generatePromptsWithFavorites = (
  favoriteIds: Set<string>,
  allPrompts: PromptDTO[],
  count: number = 3,
): PromptDTO[] => {
  const selected: PromptDTO[] = [];

  const favoritePrompts = allPrompts.filter((prompt) =>
    favoriteIds.has(String(prompt.id)),
  );
  selected.push(...favoritePrompts.slice(0, count));

  if (selected.length < count) {
    const nonFavoritePrompts = allPrompts.filter(
      (prompt) => !favoriteIds.has(String(prompt.id)),
    );
    const shuffled = [...nonFavoritePrompts].sort(() => Math.random() - 0.5);
    const neededCount = count - selected.length;

    selected.push(...shuffled.slice(0, neededCount));
  }

  return selected;
};
